import { Switch, Route, Redirect } from "wouter";
import { lazy, Suspense, type ReactNode } from "react";
import { Layout } from "@/components/Layout";
import { InstallPrompt } from "@/components/InstallPrompt";
import { PhysicsTuningSync } from "@/components/PhysicsTuningSync";
import { UpdatePrompt } from "@/components/UpdatePrompt";
import { useAuth } from "@/lib/auth";

import Landing from "@/pages/Landing";
import { SignInPage, SignUpPage } from "@/pages/SignInPage";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import NewGame from "@/pages/NewGame";
import GameDetail from "@/pages/GameDetail";
import GamesList from "@/pages/GamesList";
import PlayChooser from "@/pages/PlayChooser";
import EventsList from "@/pages/EventsList";
import NewEvent from "@/pages/NewEvent";
import EventDetail from "@/pages/EventDetail";
import TournamentBracket from "@/pages/TournamentBracket";
// Code-split: three.js is heavy (~600KB) and only used here. Keeps the
// main bundle small for the 99% of sessions that never open this route.
const VirtualPlay = lazy(() => import("@/pages/VirtualPlay"));
const VirtualSpectator = lazy(() => import("@/pages/VirtualSpectator"));
import PlayersList from "@/pages/PlayersList";
import PlayerProfile from "@/pages/PlayerProfile";
import Leaderboards from "@/pages/Leaderboards";
import OrgInsights from "@/pages/OrgInsights";
import OrgSettings from "@/pages/OrgSettings";
import Profile from "@/pages/Profile";
import PlatformAdmin from "@/pages/PlatformAdmin";
import AdminPhysics from "@/pages/AdminPhysics";
import AdminDesign from "@/pages/AdminDesign";
import Architecture from "@/pages/Architecture";
import Scorer from "@/pages/Scorer";
import Spectator from "@/pages/Spectator";
import OrgPublic from "@/pages/OrgPublic";
import PublicEvents from "@/pages/PublicEvents";
import PublicEvent from "@/pages/PublicEvent";
import NotFound from "@/pages/NotFound";

// Renders children only when the user is signed in; redirects to /sign-in
// otherwise. While the initial /me probe is in flight, shows a loader so
// we don't flash a bounce-to-sign-in for cookie-authed visitors.
function SignedInOnly({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === "loading") {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }
  if (status === "signed-out") {
    return <Redirect to="/sign-in" />;
  }
  return <>{children}</>;
}

function AppShell() {
  const { me } = useAuth();
  if (me && me.orgs.length === 0) {
    return (
      <Switch>
        <Route path="/dashboard/orgs/new" component={Onboarding} />
        <Route>
          <Redirect to="/dashboard/orgs/new" />
        </Route>
      </Switch>
    );
  }
  return (
    <Layout>
      <Switch>
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/dashboard/orgs/new" component={Onboarding} />
        <Route path="/dashboard/play" component={PlayChooser} />
        <Route path="/dashboard/games" component={GamesList} />
        <Route path="/dashboard/games/new" component={NewGame} />
        <Route path="/dashboard/games/:id" component={GameDetail} />
        <Route path="/dashboard/events" component={EventsList} />
        <Route path="/dashboard/events/new" component={NewEvent} />
        <Route path="/dashboard/events/:slug" component={EventDetail} />
        <Route
          path="/dashboard/events/:eventSlug/t/:tournamentSlug"
          component={TournamentBracket}
        />
        <Route path="/dashboard/virtual">
          <Suspense
            fallback={
              <div className="p-8 text-muted-foreground text-sm">
                Loading 3D arena…
              </div>
            }
          >
            <VirtualPlay />
          </Suspense>
        </Route>
        {/* Phase 3 multiplayer join route. Same VirtualPlay
            component — it detects the share code from useRoute
            and hydrates the joined game into activeGame instead
            of running the StartPanel. */}
        <Route path="/dashboard/virtual/play/:shareCode">
          <Suspense
            fallback={
              <div className="p-8 text-muted-foreground text-sm">
                Loading 3D arena…
              </div>
            }
          >
            <VirtualPlay />
          </Suspense>
        </Route>
        {/* Live spectator: same renderer as VirtualPlay, but
            subscribes to /api/g/:shareCode/stream and replays
            throws from the throw_made channel. Lazy-loaded for
            the same reason — three.js is huge. */}
        <Route path="/dashboard/virtual/watch/:shareCode">
          <Suspense
            fallback={
              <div className="p-8 text-muted-foreground text-sm">
                Loading 3D arena…
              </div>
            }
          >
            <VirtualSpectator />
          </Suspense>
        </Route>
        <Route path="/dashboard/players" component={PlayersList} />
        <Route path="/dashboard/players/:slug" component={PlayerProfile} />
        <Route path="/dashboard/leaderboards" component={Leaderboards} />
        <Route path="/dashboard/insights" component={OrgInsights} />
        <Route path="/dashboard/settings" component={OrgSettings} />
        <Route path="/dashboard/profile" component={Profile} />
        <Route component={Placeholder} />
      </Switch>
    </Layout>
  );
}

function Placeholder() {
  return (
    <div className="p-8 text-muted-foreground text-sm">
      Coming next in Phase 5c / 6.
    </div>
  );
}

export default function App() {
  return (
    <>
      {/* Hydrates the in-memory physics tuning singleton from the
          server blob (and re-syncs on cache invalidation). Renders
          nothing; mounted once at the root so /admin/physics edits
          land on every device, not just the one that made them. */}
      <PhysicsTuningSync />
      <AppRoutes />
      <InstallPrompt />
      {/* Floating "Update available" pill — appears bottom-right
          when a newer JS bundle has been deployed and installed
          by the service worker. Tap reloads the page. Hidden
          otherwise so it doesn't clutter the UI. */}
      <UpdatePrompt />
    </>
  );
}

function AppRoutes() {
  return (
    <Switch>
      {/* Public routes — no auth required. */}
      <Route path="/" component={Landing} />
      <Route path="/g/:shareCode" component={Spectator} />
      <Route path="/events" component={PublicEvents} />
      <Route path="/events/:orgSlug/:eventSlug" component={PublicEvent} />
      <Route path="/sign-in" component={SignInPage} />
      <Route path="/sign-up" component={SignUpPage} />

      {/* /admin namespace (platform-admin only — gated inside the pages). */}
      <Route path="/admin">
        <SignedInOnly>
          <PlatformAdmin />
        </SignedInOnly>
      </Route>
      <Route path="/admin/physics">
        <SignedInOnly>
          <AdminPhysics />
        </SignedInOnly>
      </Route>
      <Route path="/admin/design">
        <SignedInOnly>
          <AdminDesign />
        </SignedInOnly>
      </Route>
      {/* Legacy throwaway designer URLs — redirect to the unified page
          so old admin bookmarks don't 404. */}
      <Route path="/admin/scenes">
        <Redirect to="/admin/design" />
      </Route>
      <Route path="/admin/boards">
        <Redirect to="/admin/design" />
      </Route>
      <Route path="/admin/bags">
        <Redirect to="/admin/design" />
      </Route>
      <Route path="/architecture">
        <SignedInOnly>
          <Architecture />
        </SignedInOnly>
      </Route>

      {/* Scorer is auth-required at the API level. We render the page so a
          scorekeeper whose session expires keeps seeing the score; submit
          attempts surface a 401. */}
      <Route path="/score/:shareCode" component={Scorer} />

      {/*
        Two routes share the same shell. wouter v3 uses regexparam@3 which
        does NOT support `:name*` as a multi-segment wildcard — that pattern
        compiles to `[^/]+?` and silently fails to match URLs like
        /dashboard/orgs/new. The right catch-all in regexparam@3 is the
        unnamed `*`, which matches anything after the slash.
      */}
      <Route path="/dashboard">
        <SignedInOnly>
          <AppShell />
        </SignedInOnly>
      </Route>
      <Route path="/dashboard/*">
        <SignedInOnly>
          <AppShell />
        </SignedInOnly>
      </Route>

      {/* Public org landing — `/<orgSlug>`. Catches anything that wasn't
          claimed above. wouter matches in order, so the reserved top-level
          routes (above) take priority; unknown slugs render NotFound
          inside OrgPublic when the API 404s. */}
      <Route path="/:orgSlug" component={OrgPublic} />

      <Route component={NotFound} />
    </Switch>
  );
}
